import { Col, Row } from 'react-bootstrap';
import { ApiDataWrapper } from '../UIExtentions/ApiDataWrapper';
import { ApiNonSuccessError, IServer } from '../../api/models';
import { AllowedSystemLocationsView } from '../AllowedSystemLocations/AllowedSystemLocationsView';
import { PropsWithChildren, useEffect, useState } from 'react';
import { UserPrincipal } from '../../permissions/userPrincipal';
import { ServerLastDiscoveredTimestamp } from '../Shared/ServerLastDiscoveredTimestamp';
import { DateDisplay } from '../Shared/DateDisplay';
import { OperatingSystemTypeDisplay } from './OperatingSystemTypeDisplay';
import { ButtonWithIcon } from '../UIExtentions/Buttons';
import { Permission } from '../../permissions/permission';
import AppManagerClient from '../../api/AppManagerClient';
import { ServerRebootModal } from './ServerRebootModal';
import { PopupProps } from '../../areas/shared/Popup';
import { ApplicationServerManagementToast } from '../ApplicationServerManagement/ApplicationServerManagementToast';
import { ServerClientEventSummary } from '../../signalr/models';
import { ServerClientEventSummaryHandler, ServerClientEventSummaryHandlerArgs } from './ServerClientEventSummaryHandler';
import signalREventsClient from '../../signalr/SignalREventsClient';
import { EventType } from '../../constants/EventType';

export interface ServerInfoViewProps {
    userPrincipal: UserPrincipal;
    server: IServer;
    isLoading: boolean;
    isDataNotFound: boolean;
    errorMessage: string;
}
export const ServerInfoView = (props: ServerInfoViewProps) => {
    return (
        <>
            <ApiDataWrapper objectType="Details" objectName={props.server.name} isLoading={props.isLoading} isDataNotFound={props.isDataNotFound} errorMessage={props.errorMessage}>
                <ServerInfoDataView userPrincipal={props.userPrincipal} server={props.server} />
            </ApiDataWrapper>
        </>
    );
};

export interface ServerInfoDataViewProps {
    userPrincipal: UserPrincipal;
    server: IServer;
}

type ServerDetailProps = PropsWithChildren<{
    header: string;
    nullable?: boolean;
}>;

const ServerDetailRow = (props: ServerDetailProps) => {
    const nullContent = props.nullable === true ? <Col className="text-muted font-italic">Not Supported</Col> : <Col>-</Col>;
    const value = props.children ? <Col>{props.children}</Col> : <Col>{nullContent}</Col>;
    return (
        <>
            <Row>
                <Col sm={3}>{props.header}:</Col>
                {value}
            </Row>
        </>
    );
};

export const ServerInfoDataView = ({ userPrincipal, server }: ServerInfoDataViewProps) => {
    const [rebootSpin, setRebootSpin] = useState(false);
    const [serverRebootModalShow, setServerRebootModalShow] = useState(false);
    const [popupProps, setPopupProps] = useState<PopupProps | undefined>();
    const serverName = server.name;

    const showPopup = function (props: PopupProps) {
        setPopupProps(props);
    };

    const rebootTrigger = async (reason: string) => {
        try {
            const postResult = await AppManagerClient.rebootServer(serverName, reason);

            showPopup({
                header: 'Successfully scheduled reboot.',
                toCopy: postResult.correlationId,
                body: 'Reboot scheduled',
            });
        } catch (error) {
            const apiError = error as ApiNonSuccessError;
            showPopup({
                header: `Error (${apiError?.status}) while scheduling reboot.`,
                type: 'danger',
                body: `Error: ${apiError?.message} `,
            });
        }
    };

    useEffect(() => {
        const onTimeout = function (data: ServerClientEventSummary) {
            showPopup({
                header: `Operation timed out on [${data.targetServer}].`,
                body: `Operation: [${data.type}]`,
                type: 'danger',
                autohide: true,
            });
        };

        const serverRebootHandler = new ServerClientEventSummaryHandler(serverName, {
            addTypes: [EventType.ServerRebootTriggered],
            successTypes: [EventType.ServerRebootSucceeded],
            failedTypes: [EventType.ServerRebootFailed],
            emptyCallback: () => setRebootSpin(false),
            addCallback: () => setRebootSpin(true),
            timeoutCallback: (data) => onTimeout(data),
        } as ServerClientEventSummaryHandlerArgs);

        signalREventsClient.onServerRestartEvent.setHook(`ServerReboot_${serverName}`, (data) => {
            serverRebootHandler.handleItem(data);
        });
    }, [serverName]);

    return (
        <>
            <ApplicationServerManagementToast popupProps={popupProps} onClose={() => setPopupProps(undefined)} />
            <ServerRebootModal
                show={serverRebootModalShow}
                serverName={server.name}
                onHide={() => setServerRebootModalShow(false)}
                onConfirm={async (reason) => {
                    await rebootTrigger(reason);
                }}
            />
            <Row className="h-100">
                <Col>
                    <ServerDetailRow header="Name">{server.name}</ServerDetailRow>
                    <ServerDetailRow header="Environment" nullable={true}>
                        {server.environment}
                    </ServerDetailRow>
                    <ServerDetailRow header="Last Discovery" nullable={true}>
                        <ServerLastDiscoveredTimestamp lastDiscoveredTimestamp={server.lastDiscoveredTimestamp} showWikiLink={true} />
                    </ServerDetailRow>
                    <ServerDetailRow header="%LogDir% Path" nullable={true}>
                        {server.additionalInfo?.logDirPath}
                    </ServerDetailRow>
                    <ServerDetailRow header="Processor Count" nullable={true}>
                        {server.additionalInfo?.processorCount}
                    </ServerDetailRow>
                    <ServerDetailRow header="OS Type">
                        <OperatingSystemTypeDisplay
                            type={server.operatingSystemType}
                            name={server.additionalInfo?.operatingSystemName}
                            version={server.additionalInfo?.operatingSystemVersion}
                        />
                    </ServerDetailRow>
                    <ServerDetailRow header="OS Name">{server.additionalInfo?.operatingSystemName}</ServerDetailRow>
                    <ServerDetailRow header="OS Version" nullable={true}>
                        {server.additionalInfo?.operatingSystemVersion}
                    </ServerDetailRow>
                    <ServerDetailRow header="Last Reboot" nullable={true}>
                        <DateDisplay dateAsString={server.additionalInfo?.systemBootTime} />
                    </ServerDetailRow>
                    <ServerDetailRow header="System Model" nullable={true}>
                        {server.additionalInfo?.systemModel}
                    </ServerDetailRow>
                    <ServerDetailRow header="System Type" nullable={true}>
                        {server.additionalInfo?.systemType}
                    </ServerDetailRow>
                    <ServerDetailRow header="Time Zone" nullable={true}>
                        {server.additionalInfo?.timeZone}
                    </ServerDetailRow>
                    <ServerDetailRow header="Total Physical Memory" nullable={true}>
                        {server.additionalInfo?.totalPhysicalMemory}
                    </ServerDetailRow>
                    <ServerDetailRow header="Avail. Physical Memory" nullable={true}>
                        {server.additionalInfo?.availablePhysicalMemory}
                    </ServerDetailRow>
                    <ServerDetailRow header="Logon Server" nullable={true}>
                        {server.additionalInfo?.logonServer}
                    </ServerDetailRow>
                </Col>
                <Col className="d-flex flex-column">
                    <Row className="flex-grow-1">
                        <Col>
                            <AllowedSystemLocationsView userPrincipal={userPrincipal} server={server} />
                        </Col>
                    </Row>
                    <Row className="mt-auto">
                        <Col className="d-flex justify-content-end">
                            <ButtonWithIcon
                                disabled={!userPrincipal.hasPermission(Permission.Server_RestartServer)}
                                spinning={rebootSpin}
                                icon="fa-arrow-rotate-left"
                                variant="danger"
                                text="Reboot"
                                textColor="white"
                                onClick={() => {
                                    setServerRebootModalShow(true);
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
};
